import * as React from 'react';
import moment = require('moment');
import propz from 'propz';
import { AppUser } from '../../../../../views/App/App';
import { getClubProgrammeStatus, getClubProgrammeClubsCount } from 'Src/helpers/accessor/accessor';
import { formatAgesCompact, resolveAgeNamingFromContext } from 'Src/helpers/ageGroups';
import { AfterSchoolClubProgramme } from 'Src/models/clubProgramme';

const ICON_PATHS = {
  calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  flag: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7'
};

const DsIcon: React.FC<{ icon: keyof typeof ICON_PATHS; size?: number }> = ({ icon, size = 16 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.85"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={ICON_PATHS[icon]} />
  </svg>
);

type Props = {
  user: AppUser;
  programme: AfterSchoolClubProgramme;
};

const PRE_BOOKING_LABELS: Record<string, { label: string; hint: string }> = {
  HIDE: { label: 'Hidden', hint: 'Not shown before booking opens' },
  VIEW: { label: 'Preview', hint: 'Visible, booking not yet open' },
  DRAFT: { label: 'Draft booking', hint: 'Bookers can prepare a draft early' }
};

const INVITATION_LABELS: Record<string, string> = {
  DEFAULT_FROM_SCHOOL: 'School default',
  PARENT: 'Parents',
  STUDENT: 'Students',
  BOTH: 'Parents and students'
};

const fmt = (d: any) => (d ? moment(d).format('D MMM YYYY, HH:mm') : '-');

const getSelectionModeDetails = (programme: AfterSchoolClubProgramme): string => {
  const mode = String(propz.get(programme, ['selectionRules', 'mode'], 'SINGLE') || 'SINGLE').toUpperCase();
  if (mode !== 'PRIORITY') return 'Single club';

  const min = Number(propz.get(programme, ['selectionRules', 'priorityRange', 'min'], 1));
  const max = Number(propz.get(programme, ['selectionRules', 'priorityRange', 'max'], 10));
  const safeMin = Number.isFinite(min) && min > 0 ? Math.trunc(min) : 1;
  const safeMax = Number.isFinite(max) && max >= safeMin ? Math.trunc(max) : safeMin;

  return `Priority ${safeMin}-${safeMax}`;
};

const getAudienceLabel = (programme: AfterSchoolClubProgramme, user: AppUser): string => {
  const genders = propz.get(programme, ['audience', 'genders'], []);
  const ages = propz.get(programme, ['audience', 'ages'], []);
  const genderLabel =
    Array.isArray(genders) && genders.length === 1
      ? String(genders[0]) === 'MALE'
        ? 'Boys'
        : 'Girls'
      : 'Mixed';
  const naming = resolveAgeNamingFromContext(user, programme);
  const agesLabel = Array.isArray(ages) && ages.length > 0 ? formatAgesCompact(ages, naming) : 'All ages';

  return `${genderLabel} · ${agesLabel || 'All ages'}`;
};

const getInvitationsLabel = (programme: AfterSchoolClubProgramme): string => {
  const invitations = String(propz.get(programme, ['audience', 'invitations'], 'DEFAULT_FROM_SCHOOL') || 'DEFAULT_FROM_SCHOOL');
  return INVITATION_LABELS[invitations] || invitations;
};

const FieldRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="cpDrawerFieldRow">
    <div className="cpDrawerFieldLabel">{label}</div>
    <div className="cpDrawerFieldValue">{value}</div>
  </div>
);

export const ClubProgrammeSummaryTab: React.FC<Props> = ({ user, programme }) => {
  const status = getClubProgrammeStatus(programme);
  const pre = String(propz.get(programme, ['booking', 'preBookingAccess'], 'VIEW') || 'VIEW').toUpperCase();
  const preBooking = PRE_BOOKING_LABELS[pre] || PRE_BOOKING_LABELS.VIEW;

  const opensAtRaw = propz.get(programme, ['booking', 'opensAt']);
  const closesAtRaw = propz.get(programme, ['booking', 'closesAt']);
  const allowChangesAfterSubmit = Boolean(propz.get(programme, ['booking', 'allowChangesAfterSubmit'], false));
  const isActive = String(status || '').toUpperCase() === 'ACTIVE';
  const isCompleted = Boolean(propz.get(programme, ['completion', 'isCompleted'], false));
  const completedAtRaw = propz.get(programme, ['completion', 'completedAt']);
  const clubsCount = getClubProgrammeClubsCount(programme);

  const rows = [
    ['Selection mode', getSelectionModeDetails(programme)],
    ['Pre-booking visibility', `${preBooking.label} - ${preBooking.hint}`],
    ['Changes after submission', allowChangesAfterSubmit ? 'Allowed' : 'Not allowed'],
    ['Audience', getAudienceLabel(programme, user)],
    ['Invitations', getInvitationsLabel(programme)],
    ['Linked clubs', clubsCount ? `${clubsCount} clubs` : 'None yet']
  ];

  return (
    <div className="cpDrawerSummary">
      <div className="cpDrawerBookingWindow">
        <span className="cpDrawerBookingIcon">
          <DsIcon icon="calendar" size={20} />
        </span>
        <div className="cpDrawerBookingContent">
          <div className="cpDrawerBookingLabel">Booking window</div>
          <div className="cpDrawerBookingDates">
            <span>{fmt(opensAtRaw)}</span>
            <span className="cpDrawerBookingArrow">
              <DsIcon icon="arrow" size={15} />
            </span>
            <span>{fmt(closesAtRaw)}</span>
          </div>
        </div>
      </div>

      <div className="cpDrawerFieldList">
        {rows.map(([label, value]) => (
          <FieldRow key={label} label={label} value={value} />
        ))}
      </div>

      {isCompleted ? (
        <div className="cpDrawerInfoNote cpDrawerInfoNote--completed">
          <span>
            <DsIcon icon="flag" size={16} />
          </span>
          <span>Completed {fmt(completedAtRaw)}. View only for reference.</span>
        </div>
      ) : null}

      {isActive ? (
        <div className="cpDrawerLockNote">
          Selection mode, visibility and audience are locked while the programme is active.
        </div>
      ) : null}
    </div>
  );
};

ClubProgrammeSummaryTab.displayName = 'ClubProgrammeSummaryTab';